import React, { useState, useEffect, useCallback } from 'react';
import { 
  ShoppingCart, Star, Clock, Check, Plus, Minus, ArrowRight, 
  Package, Filter, Loader2, X, Briefcase, Building2, User
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { useAudience, AUDIENCE_TYPES } from '../context/AudienceContext';
import { useABTest } from '../context/ABTestContext';
import ServiceDetail from './ServiceDetail';
import PriceEstimate from './PriceEstimate';
import { apiUrl, openBookDemo } from '../utils/config';
import logger from '../utils/logger';

const Shop = ({ onBackToHome, onCheckout, serviceTypeFilter, onContactSales }) => {
  const { t, i18n } = useTranslation();
  const { addToCart, getCartCount } = useCart();
  const { audience, isB2B, isB2C, hasSelected, openSelector } = useAudience();
  const { getTestConfig, trackConversion, trackImpression, isLoaded: abTestLoaded } = useABTest();
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  // Initialize service type based on audience or prop
  const getInitialServiceType = () => {
    if (serviceTypeFilter) return serviceTypeFilter;
    if (isB2B) return 'corporate';
    if (isB2C) return 'individual';
    return 'all';
  };
  const [selectedServiceType, setSelectedServiceType] = useState(getInitialServiceType);
  const [categories, setCategories] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [hasTrackedImpression, setHasTrackedImpression] = useState(false);

  // Get A/B test configuration for pricing display
  const pricingConfig = getTestConfig('PRICING_DISPLAY');

  // Track impression when component mounts and services load
  useEffect(() => {
    if (abTestLoaded && !hasTrackedImpression && services.length > 0) {
      trackImpression('pricing_display');
      setHasTrackedImpression(true);
    }
  }, [abTestLoaded, hasTrackedImpression, services, trackImpression]);

  // Auto-set service type based on audience - runs on mount and when audience changes
  useEffect(() => {
    if (hasSelected && !serviceTypeFilter) {
      if (isB2B) {
        setSelectedServiceType('corporate');
      } else if (isB2C) {
        setSelectedServiceType('individual');
      }
    }
  }, [hasSelected, isB2B, isB2C, serviceTypeFilter]);
  
  // Also set immediately if audience is already selected on first render
  useEffect(() => {
    if (hasSelected) {
      if (isB2B) {
        setSelectedServiceType('corporate');
      } else if (isB2C) {
        setSelectedServiceType('individual');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContactSales = () => {
    if (onContactSales) {
      onContactSales();
    } else {
      onBackToHome();
      // Scroll to contact section after navigation
      setTimeout(() => {
        const contactSection = document.querySelector('#contact');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const handleServiceClick = (service) => {
    setSelectedService(service);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToList = () => {
    setSelectedService(null);
  };

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  useEffect(() => {
    if (serviceTypeFilter) {
      setSelectedServiceType(serviceTypeFilter);
    }
  }, [serviceTypeFilter]);

  const fetchServices = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(apiUrl(`services/public?lang=${i18n.language}`));
      if (response.ok) {
        const data = await response.json();
        setServices(data);
        
        // Extract unique categories
        const cats = [...new Set(data.map(s => s.category))];
        setCategories(cats);
      }
    } catch (error) {
      logger.error('Error fetching services:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredServices = services.filter(s => {
    const categoryMatch = selectedCategory === 'all' || s.category === selectedCategory;
    const typeMatch = selectedServiceType === 'all' || s.service_type === selectedServiceType;
    return categoryMatch && typeMatch;
  });

  const corporateServices = services.filter(s => s.service_type === 'corporate');
  const individualServices = services.filter(s => s.service_type === 'individual');

  const handleAddToCart = (service) => {
    addToCart(service);
    toast.success(t('shop.addedToCart', { name: service.title }));
  };

  const formatPrice = (price, priceType, subscriptionPeriod) => {
    const formatted = new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0
    }).format(price);

    if (priceType === 'subscription' && subscriptionPeriod) {
      const periodText = {
        monthly: t('shop.perMonth', '/month'),
        quarterly: t('shop.perQuarter', '/quarter'),
        yearly: t('shop.perYear', '/year')
      };
      return `${formatted}${periodText[subscriptionPeriod] || ''}`;
    }
    return formatted;
  };

  // Show service detail view if a service is selected
  if (selectedService) {
    return (
      <ServiceDetail 
        service={selectedService} 
        onBack={handleBackToList}
        onContactSales={handleContactSales}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBackToHome}
              className="text-gray-600 hover:text-blue-600 transition-colors flex items-center gap-2"
            >
              ← {t('shop.backToHome', 'Back to Home')}
            </button>
            <h1 className="text-2xl font-bold text-gray-900">{t('shop.title', 'Our Services')}</h1>
            <Button
              onClick={onCheckout}
              className="bg-blue-600 hover:bg-blue-700 relative"
              data-testid="cart-button"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              {t('shop.cart', 'Cart')}
              {getCartCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {getCartCount()}
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Audience-Specific Hero */}
        <div className="text-center mb-12">
          {isB2B ? (
            <>
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <Building2 className="w-4 h-4" />
                {t('shop.b2bBadge', 'Enterprise Solutions')}
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('shop.b2bTitle', 'Corporate Coaching Programs')}
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-6">
                {t('shop.b2bSubtitle', 'Scalable leadership development programs designed for organizations. Contact our sales team for custom enterprise packages.')}
              </p>
            </>
          ) : isB2C ? (
            <>
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <User className="w-4 h-4" />
                {t('shop.b2cBadge', 'Personal Coaching')}
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('shop.b2cTitle', 'Individual Coaching Packages')}
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-6">
                {t('shop.b2cSubtitle', 'Personalized 1:1 coaching sessions to accelerate your personal and professional growth. Choose your package and start today.')}
              </p>
            </>
          ) : (
            <>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('shop.heroTitle', 'Transform Your Leadership')}
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                {t('shop.heroSubtitle', 'Choose from our range of coaching programs and services designed to elevate your team\'s performance.')}
              </p>
            </>
          )}
        </div>

        {/* Service Type Tabs - Show differently based on audience */}
        {!hasSelected || selectedServiceType === 'all' ? (
          <div className="flex items-center justify-center gap-2 mb-8">
            <Button
              variant={selectedServiceType === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedServiceType('all')}
              className={`px-6 py-3 ${selectedServiceType === 'all' ? 'bg-blue-600 hover:bg-blue-700' : 'hover:bg-gray-100'}`}
              data-testid="filter-all"
            >
              {t('shop.allServices', 'All Services')}
              <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                {services.length}
              </span>
            </Button>
            <Button
              variant={selectedServiceType === 'corporate' ? 'default' : 'outline'}
              onClick={() => setSelectedServiceType('corporate')}
              className={`px-6 py-3 ${selectedServiceType === 'corporate' ? 'bg-blue-600 hover:bg-blue-700' : 'hover:bg-gray-100'}`}
              data-testid="filter-corporate"
            >
              <Building2 className="w-4 h-4 mr-2" />
              {t('shop.corporate', 'Corporate')}
              <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                {corporateServices.length}
              </span>
            </Button>
            <Button
              variant={selectedServiceType === 'individual' ? 'default' : 'outline'}
              onClick={() => setSelectedServiceType('individual')}
              className={`px-6 py-3 ${selectedServiceType === 'individual' ? 'bg-emerald-600 hover:bg-emerald-700' : 'hover:bg-gray-100'}`}
              data-testid="filter-individual"
            >
              <User className="w-4 h-4 mr-2" />
              {t('shop.individual', 'Individual')}
              <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                {individualServices.length}
              </span>
            </Button>
          </div>
        ) : (
          /* If audience is selected, show only that audience's services */
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className={`px-6 py-3 rounded-lg font-semibold ${
              isB2B ? 'bg-blue-600 text-white' : 'bg-emerald-600 text-white'
            }`}>
              {isB2B ? (
                <span className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  {t('shop.corporate', 'Corporate')} ({corporateServices.length})
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  {t('shop.individual', 'Individual')} ({individualServices.length})
                </span>
              )}
            </div>
          </div>
        )}

        {/* Category Filter */}
        {categories.length > 1 && (
          <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedCategory('all')}
              className={selectedCategory === 'all' ? 'bg-gray-700' : ''}
              size="sm"
            >
              {t('shop.allCategories', 'All Categories')}
            </Button>
            {categories.map(cat => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(cat)}
                className={selectedCategory === cat ? 'bg-gray-700' : ''}
                size="sm"
              >
                {cat}
              </Button>
            ))}
          </div>
        )}

        {/* Services Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : filteredServices.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600">{t('shop.noServices', 'No services available')}</h3>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredServices.map((service) => (
              <div
                key={service.id}
                className={`bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col cursor-pointer group ${
                  service.featured ? 'ring-2 ring-blue-500' : ''
                }`}
                data-testid={`service-card-${service.id}`}
                onClick={() => handleServiceClick(service)}
              >
                {/* Featured Badge */}
                {service.featured && (
                  <div className="bg-blue-600 text-white text-center py-2 text-sm font-medium">
                    <Star className="w-4 h-4 inline mr-1" />
                    {t('shop.featured', 'Featured')}
                  </div>
                )}

                {/* Image */}
                <div className="h-48 bg-gradient-to-br from-blue-50 to-blue-100 relative overflow-hidden">
                  {service.image ? (
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-16 h-16 text-blue-300" />
                    </div>
                  )}
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-blue-700 text-sm font-medium rounded-full">
                      {service.category}
                    </span>
                  </div>
                  {/* View Details Overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <span className="bg-white text-gray-900 px-4 py-2 rounded-full font-medium shadow-lg">
                      {t('shop.viewDetails', 'View Details')}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 flex-1 line-clamp-3">{service.description}</p>

                  {/* Features */}
                  {service.features?.length > 0 && (
                    <ul className="space-y-2 mb-4">
                      {service.features.slice(0, 4).map((feature, idx) => (
                        <li key={`${service.id}-feat-${idx}`} className="flex items-start gap-2 text-sm text-gray-600">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          {feature}
                        </li>
                      ))}
                      {service.features.length > 4 && (
                        <li className="text-sm text-blue-600">+{service.features.length - 4} more</li>
                      )}
                    </ul>
                  )}

                  {/* Duration */}
                  {service.duration && (
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                      <Clock className="w-4 h-4" />
                      {service.duration}
                    </div>
                  )}

                  {/* Price & CTA */}
                  <div className="mt-auto pt-4 border-t border-gray-100">
                    {service.service_type === 'corporate' ? (
                      /* Corporate: No price, full-width Contact Sales button */
                      <div className="space-y-3">
                        <p className="text-sm text-gray-500 text-center">
                          {t('shop.customPricing', 'Custom pricing based on your needs')}
                        </p>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleContactSales();
                          }}
                          className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg font-semibold"
                          data-testid={`contact-sales-${service.id}`}
                        >
                          <Briefcase className="w-5 h-5 mr-2" />
                          {t('shop.contactSales', 'Contact Sales')}
                        </Button>
                      </div>
                    ) : (
                      /* Individual: Always show price with Add to Cart 
                         A/B test only affects: CTA text, discount badge display */
                      <div className="flex items-end justify-between">
                        <div>
                          {pricingConfig?.showDiscount && (
                            <span className="text-sm text-gray-400 line-through mr-2">
                              ₺{Math.round(service.price * 1.2).toLocaleString('tr-TR')}
                            </span>
                          )}
                          <span className="text-3xl font-bold text-gray-900">
                            {formatPrice(service.price, service.price_type, service.subscription_period)}
                          </span>
                          {service.price_type === 'one-time' && (
                            <span className="text-gray-500 text-sm ml-1">{t('shop.oneTime', 'one-time')}</span>
                          )}
                          {pricingConfig?.showDiscount && (
                            <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                              20% OFF
                            </span>
                          )}
                          <div className="text-xs text-gray-500 mt-1">
                            {i18n.language === 'tr' ? '+ %20 KDV' : '+ 20% VAT'}
                          </div>
                          <div className="mt-1">
                            <PriceEstimate amount={service.price} />
                          </div>
                        </div>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Track conversion
                            trackConversion('pricing_display', 'add_to_cart', {
                              serviceId: service.id,
                              variant: pricingConfig?.variant,
                              price: service.price
                            });
                            handleAddToCart(service);
                          }}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`add-to-cart-${service.id}`}
                        >
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          {pricingConfig?.ctaTextKey ? t(pricingConfig.ctaTextKey, t('shop.addToCart', 'Add to Cart')) : t('shop.addToCart', 'Add to Cart')}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* B2B Call to Action Banner */}
        {isB2B && filteredServices.length > 0 && (
          <div className="mt-12 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 md:p-12 text-white text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              {t('shop.b2bCta.title', 'Need a Custom Enterprise Solution?')}
            </h3>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              {t('shop.b2bCta.subtitle', 'Our team will work with you to design a coaching program tailored to your organization\'s unique needs and goals.')}
            </p>
            <Button
              onClick={openBookDemo}
              size="lg"
              className="bg-white text-blue-700 hover:bg-blue-50 px-8 py-6 text-lg font-semibold"
            >
              <Briefcase className="w-5 h-5 mr-2" />
              {t('shop.b2bCta.button', 'Book a Demo')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* B2C Benefits Banner */}
        {isB2C && filteredServices.length > 0 && (
          <div className="mt-12 bg-gradient-to-r from-emerald-600 to-teal-700 rounded-2xl p-8 md:p-12 text-white">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-center">
                {t('shop.b2cBenefits.title', 'Why Choose Our Coaching?')}
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Star className="w-6 h-6" />
                  </div>
                  <h4 className="font-semibold mb-1">{t('shop.b2cBenefits.benefit1.title', 'Expert Coaches')}</h4>
                  <p className="text-sm text-emerald-100">{t('shop.b2cBenefits.benefit1.desc', 'Certified professionals with proven track records')}</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Clock className="w-6 h-6" />
                  </div>
                  <h4 className="font-semibold mb-1">{t('shop.b2cBenefits.benefit2.title', 'Flexible Scheduling')}</h4>
                  <p className="text-sm text-emerald-100">{t('shop.b2cBenefits.benefit2.desc', 'Book sessions that fit your busy schedule')}</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Check className="w-6 h-6" />
                  </div>
                  <h4 className="font-semibold mb-1">{t('shop.b2cBenefits.benefit3.title', 'Guaranteed Results')}</h4>
                  <p className="text-sm text-emerald-100">{t('shop.b2cBenefits.benefit3.desc', '85% of clients achieve their goals')}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Shop;
